import React, { useState, useMemo, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Checkbox,
  Button,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { fetchDashboardData, clearDashboardData, fetchBootCampAndFormList, closeDashboard, fetchDashboardConfig } from "../../store/dashboard";
import DynamicTable from "../../components/Table/DynamicTable";
import Loader from "../../components/Loader/Loader";

import { getStatus, prepareBootcampOptions, prepareFormOptions, ROLE_TYPES } from "../../utils/utils";
import { HelpTexts } from "../../constants/constants";
import UserTableAvatar from "../../components/UserTableAvatar/UserTableAvatar";
import AutoCompleteTagInput from "../../components/AutoCompleteTagInput/AutoCompleteTagInput";
import NoDataAvailable from "../../components/NoDataAvailable/NoDataAvailable";
import { useLocation, useNavigate } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(tz);

const { NON_ACTIVE_ATTENDEE } = HelpTexts;


const DASHBOARD_TABS = {
  CLASSROOM_PHASE: "CLASSROOM_PHASE",
  SHADOW_PROJECT_PHASE: "SHADOW_PROJECT_PHASE",
  OVERALL: "OVERALL",
};

const FORM_TYPES = {
  CLASSROOM: "CLASSROOM_ASSESSMENT_PERFORMANCE",
  CLASSROOM_BEHAVIOUR: "CLASSROOM_BEHAVIOUR",
  SHADOW_PERFORMANCE: "SHADOW_PROJECT_ASSESSMENT_PERFORMANCE",
  SHADOW_BEHAVIOUR: "SHADOW_PROJECT_BEHAVIOUR",
};

const TAB_MAPPINGS = {
  [DASHBOARD_TABS.CLASSROOM_PHASE]: [
    FORM_TYPES.CLASSROOM,
    FORM_TYPES.CLASSROOM_BEHAVIOUR,
  ],
  [DASHBOARD_TABS.SHADOW_PROJECT_PHASE]: [
    FORM_TYPES.SHADOW_PERFORMANCE,
    FORM_TYPES.SHADOW_BEHAVIOUR,
  ],
  [DASHBOARD_TABS.OVERALL]: [
    FORM_TYPES.CLASSROOM,
    FORM_TYPES.CLASSROOM_BEHAVIOUR,
    FORM_TYPES.SHADOW_PERFORMANCE,
    FORM_TYPES.SHADOW_BEHAVIOUR,
  ],
};
const FORM_DISPLAY_NAMES = {
  [FORM_TYPES.CLASSROOM]: "Technical Assessment",
  [FORM_TYPES.CLASSROOM_BEHAVIOUR]: "Behavioural Assessment (Phase I)",
  [FORM_TYPES.SHADOW_PERFORMANCE]: "Project Assessment",
  [FORM_TYPES.SHADOW_BEHAVIOUR]: "Behavioural Assessment (Phase II)",
};

const ZONE_COLORS = {
  GREEN: "#388e3c",
  AMBER: "#f9a825",
  RED: "#d32f2f",
  PURPLE: "#b32682",
  BORDER: "#e0e0e0",
};
const parseConfig = (configStr) => {
  if (!configStr) return null;
  const parts = configStr.split(',').map(range => range.split('-').map(Number));

  return {
    red: { min: parts[0][0], max: parts[0][1] },
    amber: { min: parts[1][0], max: parts[1][1] },
    green: { min: parts[2][0], max: parts[2][1] }
  };
};

const getNormalizedValue = (value) => {
  return typeof value === "string" ? value.toLowerCase() : value;
};

const compareValues = (a, b, direction) => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  const aValue = getNormalizedValue(a);
  const bValue = getNormalizedValue(b);

  if (aValue < bValue) return direction === "asc" ? -1 : 1;
  if (aValue > bValue) return direction === "asc" ? 1 : -1;
  return 0;
};

const sortData = (data, key, direction) => {
  if (!key) return data;

  return [...data].sort((a, b) => compareValues(a[key], b[key], direction));
};

const createNameEmailColumn = (attendeeStatuses) => ({
  key: "fullName",
  label: "Newer Name",
  minWidth: "16rem",
  sortable: true,
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.fullName}
      imgSrc={row.profilePictureUrl}
      href={row.newerId}
      label={row.fullName}
      subLabel={row.newerEmail}
      deleted={row.userDeleted}
      userId={row.newerId}
      tooltip={
        row.userDeleted
          ? NON_ACTIVE_ATTENDEE(
            getStatus(attendeeStatuses, row.attendeeStatus),
            dayjs(row.statusUpdatedDate)
              .tz(dayjs.tz.guess())
              .format("DD MMM YYYY")
          )
          : null
      }
    />
  ),
});

const StatusHeader = ({ label, count, color }) => (
  <Card sx={{ mb: 1, boxShadow: 0, border: "1px solid #d1d1d1", borderRadius: "4px" }}>
    <CardContent sx={{ textAlign: "center", py: "10px !important" }}>
      <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: "bold", color }}>
        {count}
      </Typography>
    </CardContent>
  </Card>
);

StatusHeader.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
};

const SortableTableWrapper = memo(({ data, columns, title, ...props }) => {
  const [sortedRows, setSortedRows] = useState(data);

  useEffect(() => {
    setSortedRows(data);
  }, [data]);

  const handleLocalSort = (key, direction) => {
    const sorted = [...sortedRows].sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      if (key === 'score' || key.includes('Score')) {
        return direction === "asc" ? valA - valB : valB - valA;
      }

      valA = String(valA || "").toLowerCase();
      valB = String(valB || "").toLowerCase();
      return direction === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
    setSortedRows(sorted);
  };

  return (
    <DynamicTable
      {...props}
      title={title}
      columns={columns}
      rows={sortedRows}
      onSort={handleLocalSort}
    />
  );
});

const PerformanceDashboard = ({ preselectedBootcampId, isClose=()=>{} }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { roles } = useSelector((state) => state.security);
  const { filterList, permissions } = useSelector((state) => state.dashboard);
  const [activeTab, setActiveTab] = useState(DASHBOARD_TABS.CLASSROOM_PHASE);
  const [selectedBootcampIds, setSelectedBootcampIds] = useState([]);
  const [selectedFormIds, setSelectedFormIds] = useState([]);
  const [summarySort, setSummarySort] = useState({
    formKey: null,
    column: "",
    direction: "asc"
  });
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const handleLocalSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const isHr = ROLE_TYPES.isHr(roles);
  const isFormsDashboard = location.pathname === "/performanceFeedbacks/dashboard";

  const { data: dashboardData, loading, dashboardConfig } = useSelector((state) => state.dashboard);
  const attendeeStatuses = useSelector((state) => state?.utility?.constants?.attendeeStatuses);
  const isSpoc = useMemo(() => permissions?.spoc === true, [permissions]);
  const isAttendee = useMemo(() => permissions?.attendee === true, [permissions]);
  const userRole = useMemo(() => {
    if (isSpoc) return { asSpoc: true };
    if (isHr) return { asHr: true };
    if (isAttendee) return { asAttendee: true }
    return {};
  }, [permissions?.spoc, isHr, permissions?.attendee]);
  const bootcampOptions = useMemo(
    () => prepareBootcampOptions(filterList),
    [filterList]
  );


  const formOptions = useMemo(
    () =>
      prepareFormOptions(
        filterList,
        selectedBootcampIds.map((b) => b.id)
      ),
    [filterList, selectedBootcampIds]
  );

  const dynamicZones = useMemo(() => {
    if (!dashboardConfig) {
      return [
        { label: "Red Zone", range: "0 - 2.5", color: ZONE_COLORS.RED, min: 0, max: 2.5 },
        { label: "Amber Zone", range: "2.6 - 3.7", color: ZONE_COLORS.AMBER, min: 2.6, max: 3.7 },
        { label: "Green Zone", range: "3.8 - 5", color: ZONE_COLORS.GREEN, min: 3.8, max: 5 },
      ];
    }

    const parsed = parseConfig(dashboardConfig);
    return [
      { label: "Red Zone", range: `${parsed.red.min} - ${parsed.red.max}`, color: ZONE_COLORS.RED, ...parsed.red },
      { label: "Amber Zone", range: `${parsed.amber.min} - ${parsed.amber.max}`, color: ZONE_COLORS.AMBER, ...parsed.amber },
      { label: "Green Zone", range: `${parsed.green.min} - ${parsed.green.max}`, color: ZONE_COLORS.GREEN, ...parsed.green },
    ];
  }, [dashboardConfig]);

  const getZone = (score) => {
    const s = parseFloat(score);
    const red = dynamicZones[0];
    const amber = dynamicZones[1];
    const green = dynamicZones[2];

    if (s >= green.min) return "GREEN";
    if (s >= amber.min) return "AMBER";
    return "RED";
  };


  useEffect(() => {
    dispatch(fetchDashboardConfig());
    dispatch(fetchBootCampAndFormList());
  }, [dispatch])



  useEffect(() => {
    if (!bootcampOptions.length || !preselectedBootcampId) return;
    if (Array.isArray(preselectedBootcampId)) {
      const selected = bootcampOptions.filter((b) =>
        preselectedBootcampId.includes(b.id)
      );

      if (selected.length) {
        setSelectedBootcampIds(selected);
      }
    }
    else {
      const selected = bootcampOptions.find(
        (b) => b.id === preselectedBootcampId
      );
      if (selected) {
        setSelectedBootcampIds([selected]);
      }
    }
  }, [preselectedBootcampId, bootcampOptions]);

  useEffect(() => {
    if (selectedBootcampIds.length > 0) {
      dispatch(
        fetchDashboardData({
          bootcampIds: selectedBootcampIds.map((b) => b.id),
          formId: selectedFormIds.map((f) => f.id),
          paginated: false,
          ...userRole
        })
      );
    } else {
      dispatch(clearDashboardData());
    }
    return () => {
      if (selectedBootcampIds.length === 0) dispatch(clearDashboardData());
    };
  }, [dispatch, selectedBootcampIds, selectedFormIds]);

  const mapUserData = (item) => {
    const user = item.user || {};
    return {
      fullName: user.fullName || "N/A",
      newerEmail: user.email || "N/A",
      profilePictureUrl: user.profilePictureUrl || "",
      newerId: user.employeeCode || user.id,
      userDeleted: user.deleted || false,
      attendeeStatus: item.attendeeStatus,
      statusUpdatedDate: item.statusUpdatedDate,
      competency: user.competency || "",
    };
  };

  const processedOverallData = useMemo(() => {
    const results = dashboardData?.results || [];

    return results.map((item) => {
      const ts = item.scoresByFormType?.[FORM_TYPES.CLASSROOM] ?? 0;
      const b1 = item.scoresByFormType?.[FORM_TYPES.CLASSROOM_BEHAVIOUR] ?? 0;
      const ps = item.scoresByFormType?.[FORM_TYPES.SHADOW_PERFORMANCE] ?? 0;
      const b2 = item.scoresByFormType?.[FORM_TYPES.SHADOW_BEHAVIOUR] ?? 0;

      return {
        ...mapUserData(item),
        technicalScore: ts,
        technicalZone: getZone(ts),
        beh1Score: b1,
        beh1Zone: getZone(b1),
        projectScore: ps,
        projectZone: getZone(ps),
        beh2Score: b2,
        beh2Zone: getZone(b2),
      };
    });
  }, [dashboardData]);

  const sortedOverallData = useMemo(() => {
    return sortData(processedOverallData, sortConfig.key, sortConfig.direction);
  }, [processedOverallData, sortConfig]);



  const summaryColumns = useMemo(() => [
    createNameEmailColumn(attendeeStatuses),
    { key: "competency", label: "Competency" },
    {
      key: "score",
      label: "Score",
      sortable: true,
      render: (v) => {
        const zone = getZone(v);
        return (
          <Chip
            label={Number(v).toFixed(1)}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: ZONE_COLORS[zone],
              color: "#fff",
              minWidth: "40px"
            }}
          />
        );
      },
    },
  ], [attendeeStatuses]);

  const overallTableColumns = useMemo(() => [
    {
      ...createNameEmailColumn(attendeeStatuses),
      sortable: true
    },
    {
      key: "competency",
      label: "Competency",
      minWidth: "8rem",
      sortable: true
    },
    {
      key: "technicalScore",
      label: "Technical Assessment(s) Score",
      minWidth: "12rem",
      sortable: true,
      render: (v) => {
        const zone = getZone(v);
        return (
          <Chip
            label={Number(v).toFixed(1)}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: ZONE_COLORS[zone],
              color: "#fff",
              minWidth: "45px"
            }}
          />
        );
      }
    },
    {
      key: "beh1Score",
      label: "Behavioural Score (Phase I)",
      minWidth: "12rem",
      sortable: true,
      render: (v) => {
        const zone = getZone(v);
        return (
          <Chip
            label={Number(v).toFixed(1)}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: ZONE_COLORS[zone],
              color: "#fff",
              minWidth: "45px"
            }}
          />
        );
      }
    },
    {
      key: "projectScore",
      label: "Project Assessment Score",
      minWidth: "12rem",
      sortable: true,
      render: (v) => {
        const zone = getZone(v);
        return (
          <Chip
            label={Number(v).toFixed(1)}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: ZONE_COLORS[zone],
              color: "#fff",
              minWidth: "45px"
            }}
          />
        );
      }
    },
    {
      key: "beh2Score",
      label: "Behavioural Score (Phase II)",
      minWidth: "12rem",
      sortable: true,
      render: (v) => {
        const zone = getZone(v);
        return (
          <Chip
            label={Number(v).toFixed(1)}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: ZONE_COLORS[zone],
              color: "#fff",
              minWidth: "45px"
            }}
          />
        );
      }
    },
  ], [attendeeStatuses]);
  
const compareRows = (a, b, column, direction) => {
  let valA = a[column];
  let valB = b[column];

  if (column === "score") {
    return direction === "asc" ? valA - valB : valB - valA;
  }

  valA = String(valA || "").toLowerCase();
  valB = String(valB || "").toLowerCase();

  if (valA < valB) return direction === "asc" ? -1 : 1;
  if (valA > valB) return direction === "asc" ? 1 : -1;
  return 0;
};

const processFormRows = (results, formKey, summarySort) => {
  const rows = results.map((item) => ({
    ...mapUserData(item),
    score: item.scoresByFormType?.[formKey] || 0,
  }));

  if (summarySort.formKey === formKey) {
    const { column, direction } = summarySort;
    rows.sort((a, b) => compareRows(a, b, column, direction));
  }

  return {
    high: rows.filter((r) => r.score >= 3.8),
    medium: rows.filter((r) => r.score >= 2.6 && r.score < 3.8),
    low: rows.filter((r) => r.score < 2.6),
  };
};

const zoneCategorizedData = useMemo(() => {
  const results = dashboardData?.results || [];
  if (!results.length) return {};

  const categories = {};

  Object.values(FORM_TYPES).forEach((formKey) => {
    categories[formKey] = processFormRows(results, formKey, summarySort);
  });

  return categories;
}, [dashboardData, summarySort]);

  const handleCloseDashboard = () => {
    const path = location.pathname;
    isClose()
    dispatch(closeDashboard())
    navigate(path);
  }

  const renderDashboardContent = () => {
    if (loading) return <Loader />;

    const activeFormKeys = TAB_MAPPINGS[activeTab] || [];
    const results = dashboardData?.results || [];

    if (!results.length) return <NoDataAvailable message="No assessment data found." />;

    return (
      <Box sx={{ mt: 3 }}>
        {activeFormKeys.map((formKey) => {
          const zoneData = zoneCategorizedData[formKey];
          if (!zoneData) return null;

          return (
            <Box key={formKey} sx={{ mb: 6 }}>
              <Typography variant="font_20_bold" sx={{ mb: 2, display: "block" }}>
                {FORM_DISPLAY_NAMES[formKey]}
              </Typography>

              <Grid container spacing={3}>
                {[
                  { label: "Green", data: zoneData.high, color: ZONE_COLORS.GREEN },
                  { label: "Amber", data: zoneData.medium, color: ZONE_COLORS.AMBER },
                  { label: "Red", data: zoneData.low, color: ZONE_COLORS.RED },
                ].map((zone) => (
                  <Grid item xs={12} md={4} key={zone.label}>
                    <StatusHeader label={zone.label} count={zone.data.length} color={zone.color} />
                    <Paper elevation={0} sx={{ borderTop: `10px solid ${zone.color}`, borderRadius: "4px 4px 0 0", borderLeft: '1px solid #eee', borderRight: '1px solid #eee' }}>
                      <Box sx={{ height: "300px", overflowY: "auto", borderBottom: "1px solid  #eee" }}>
                        <SortableTableWrapper
                          columns={summaryColumns}
                          data={zone.data}
                          showPagination={false}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </Box>
    );
  };
  return (
    <Grid container className="main-container">
      <Grid item xs={12} className="header-container">
        <Typography variant="font_21_bold">Performance Dashboard</Typography>
        {!isFormsDashboard && <Box className="header-right-icons">
          <Button variant="outlined" color="secondary" onClick={handleCloseDashboard}>
            <Typography variant="outlineBtnLabel">Back</Typography>
          </Button>
        </Box>}

      </Grid>

      <Grid item md={12} className="body-container">
        <Grid container className="table-parent-container">
          <Paper elevation={0} className="flex-container-column topic-paper-container" sx={{ overflow: "scroll" }}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">

              <Grid item md={6}>
                <AutoCompleteTagInput
                  label="Select Bootcamps"
                  placeholder={selectedBootcampIds?.length ? null : "Select Bootcamp"}
                  options={bootcampOptions}
                  selectedValue={selectedBootcampIds}
                  getOptionLabel={(option) => option.name}
                  disabled={isAttendee || isSpoc}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, params) => {
                    const filter = createFilterOptions();
                    const filtered = filter(options, params);
                    if (bootcampOptions.length === 0) {
                      return [{ name: "No options", all: false }];
                    }
                    return [{ name: "Select All", all: true }, ...filtered];
                  }}
                  onChange={(event, newValue) => {

                    if (newValue.find((option) => option.all)) {
                      const isCurrentlyAllSelected = selectedBootcampIds?.length === bootcampOptions?.length;
                      const nextBootcamps = isCurrentlyAllSelected ? [] : bootcampOptions;

                      setSelectedBootcampIds(nextBootcamps);
                      setSelectedFormIds([]);
                      return;
                    }


                    const cleanValue = newValue.filter((option) => !option.all);
                    setSelectedBootcampIds(cleanValue);
                    setSelectedFormIds([]);
                  }}
                  required={true}
                  error={!selectedBootcampIds?.length}
                  helperText={!selectedBootcampIds?.length ? "Atleast 1 Bootcamp must be selected" : ""}
                  renderOption={(props, option) => {
                    const isOptionSelected =
                      (option.all && selectedBootcampIds?.length === bootcampOptions?.length) ||
                      selectedBootcampIds?.some((e) => e?.id === option?.id);

                    return (
                      <li
                        {...props}
                        key={option.all ? "select-all" : option.id}
                        style={{ paddingBottom: 1, paddingTop: 1 }}
                        className={isOptionSelected ? "multi-tag-edit-list-selected" : "multi-tag-style"}
                      >
                        <Checkbox
                          size="small"
                          checked={isOptionSelected}
                          color="secondary"
                          sx={{
                            marginRight: 0.2,
                            marginTop: "0.2rem",
                            padding: "0.063rem 0.438rem",
                            "& .MuiSvgIcon-root": { fontSize: 15 },
                          }}
                        />
                        <Box>
                          <Typography variant="font_16">{option.name}</Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                      />
                    ))
                  }
                />
              </Grid>



              <Grid item md={4} >
                <Box display="grid" justifyContent="end"
                  sx={{ rowGap: "8px" }} >
                  {dynamicZones.map((z) => (
                    <Box key={z.label} display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: z.color,
                        }}
                      />
                      <Typography variant="body2">
                        {z.label}: <strong>{z.range}</strong>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              textColor="secondary"
              indicatorColor="secondary"
              centered
            >
              <Tab label="Phase I - Classroom Period" value={DASHBOARD_TABS.CLASSROOM_PHASE} />
              <Tab label="Phase II - Project Shadow Period" value={DASHBOARD_TABS.SHADOW_PROJECT_PHASE} />
              <Tab label="Overall Dashboard" value={DASHBOARD_TABS.OVERALL} />
            </Tabs>
            {renderDashboardContent()}
            <Box sx={{ mt: 6 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Detailed Score Matrix</Typography>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ maxHeight: "800px", overflowY: "auto" }}>
                  <DynamicTable
                    columns={overallTableColumns}
                    rows={sortedOverallData}
                    showPagination={false}
                    onSort={handleLocalSort}
                  />
                </Box>

              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
};

PerformanceDashboard.propTypes = {
  preselectedBootcampId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PerformanceDashboard;